import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { addToWishlist, removeFromWishlist } from "../../../redux/actions/wishlist";
import { AiFillHeart, AiOutlineHeart } from "react-icons/ai";
import { Link, useNavigate } from "react-router-dom";
//import Ratings from "../../Products/Ratings";
import { getCoordinatesFromPin } from "../../../utils/pincode";
import { calculateDistance, getMaxServiceableDistance } from "../../../utils/distance";

const ProductCard = ({ data, isEvent }) => {
  const { wishlist } = useSelector((state) => state.wishlist);
  const { pinCode, coordinates: userCoordinates } = useSelector((state) => state.user);
  const [click, setClick] = useState(false);
  const dispatch = useDispatch();

  // State variables for distance
  const [distanceCategory, setDistanceCategory] = useState(null);
  const [loadingDistance, setLoadingDistance] = useState(false);
  const [errorDistance, setErrorDistance] = useState(null);
  const [currentImage, setCurrentImage] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [isServiceable, setIsServiceable] = useState(true);
  // distance value is represented to users by distanceCategory; raw distance not needed here

  useEffect(() => {
    let interval;
    if (!isHovered && data.images?.length > 1) {
      interval = setInterval(() => {
        setCurrentImage(prev => (prev + 1) % data.images.length);
      }, 5000);
    }
    return () => interval && clearInterval(interval);
  }, [isHovered, data.images]);

  useEffect(() => {
    const fetchDistance = async () => {
      if (!pinCode || !userCoordinates || !data?.shop?.zipCode) {
        setIsServiceable(false);
        return;
      }

      try {
        const sellerCoordinates = await getCoordinatesFromPin(data.shop.zipCode);
        const calculatedDistance = calculateDistance(
          userCoordinates.lat,
          userCoordinates.lon,
          sellerCoordinates.lat,
          sellerCoordinates.lon
        );
  // raw distance stored in UI via distanceCategory; no separate raw state required

        const maxDistance = getMaxServiceableDistance(data.serviceableLocation);
        setIsServiceable(maxDistance === Infinity || calculatedDistance <= maxDistance);
      } catch (error) {
        console.error("Distance calculation error:", error);
        setIsServiceable(false);
      }
    };

    fetchDistance();
  }, [pinCode, userCoordinates, data.shop, data.serviceableLocation]);

  const handleMouseEnter = () => {
    setIsHovered(true);
    setCurrentImage(0); // Reset to first image immediately on hover
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  useEffect(() => {
    if (wishlist && wishlist.find((i) => i._id === data._id)) {
      setClick(true);
    } else {
      setClick(false);
    }
  }, [wishlist, data._id]);

  const removeFromWishlistHandler = (data) => {
    setClick(!click);
    dispatch(removeFromWishlist(data));
  };

  const addToWishlistHandler = (data) => {
    setClick(!click);
    dispatch(addToWishlist(data));
  };

  const getPriceRange = () => {
    if (
      data &&
      data.quantitySlabsWithCommission &&
      Array.isArray(data.quantitySlabsWithCommission) &&
      data.quantitySlabsWithCommission.length > 0
    ) {
      const prices = data.quantitySlabsWithCommission
        .map((slab) => slab.discountPriceWithCommission)
        .filter((price) => !isNaN(price) && price !== undefined && price !== null);

      if (prices.length > 0) {
        const minPrice = Math.min(...prices);
        const maxPrice = Math.max(...prices);
        return `Rs ${minPrice.toFixed(2)} - Rs ${maxPrice.toFixed(2)}`;
      }
    }

    // If quantitySlabsWithCommission is not valid or no valid prices found
    if (data && data.discountPriceWithCommission) {
      return `Rs ${data.discountPriceWithCommission}`;
    }

    // Default fallback if all else fails
    return `Rs N/A`;
  };

  // useEffect to calculate distance
  useEffect(() => {
    const fetchDistance = async () => {
      // Reset states
      setDistanceCategory(null);
      setErrorDistance(null);
      setLoadingDistance(true);

      if (!pinCode || !userCoordinates) {
        setErrorDistance("Set pin code to get distance");
        setLoadingDistance(false);
        return;
      }

      if (!data.shop || !data.shop.zipCode) {
        setErrorDistance("Seller pin code not available");
        setLoadingDistance(false);
        return;
      }

      try {
        const sellerPinCode = data.shop.zipCode;

        const sellerCoordinates = await getCoordinatesFromPin(sellerPinCode);

        const distance = calculateDistance(
          userCoordinates.lat,
          userCoordinates.lon,
          sellerCoordinates.lat,
          sellerCoordinates.lon
        );

        let category = "N/A";

        if (distance <= 10) {
          category = "Within 10Km / Quick Delivery";
        } else if (distance > 10 && distance <= 50) {
          category = "Within 10-50km / Normal Delivery";
        } else if (distance > 50 && distance <= 100) {
          category = "Within 50-100km / 2 Day Delivery";
        } else if (distance > 100 && distance <= 1000) {
          category = "Within 100-1000km / 3 Day Delivery";
        } else {
          category = "Above 1000km / 1 Week Delivery";
        }

        setDistanceCategory(category);
        setErrorDistance(null);
      } catch (error) {
        console.error("Error calculating distance:", error);
        setDistanceCategory(null);
        setErrorDistance("Error calculating distance");
      } finally {
        setLoadingDistance(false);
      }
    };

    fetchDistance();
  }, [pinCode, userCoordinates, data.shop]);

  const navigate = useNavigate();

  return (
    <div className="w-full bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300 flex flex-col relative">
      <Link
      to={`/product/${data._id}`}
        className="flex-shrink-0"
        onClick={(e) => {
          // If this is an event link and we need the query param for client behavior,
          // prevent default navigation and push the parameter after navigation to canonical URL
          if (isEvent) {
            e.preventDefault();
        // Navigate to the canonical product URL and pass event flag in location state
        // so we avoid creating query-parameter variants that could be crawled.
        navigate(`/product/${data._id}`, { state: { isEvent: true } });
          }
        }}
      >
        <div 
          className="relative w-full h-56 md:h-48 bg-white flex items-center justify-center"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {data.images?.map((img, index) => (
            <img
              key={img.url}
              src={img.url || "/placeholder-image.png"}
              alt={data.name}
              className={`max-h-full max-w-full object-contain absolute transition-opacity duration-500 ${
                index === currentImage ? 'opacity-100' : 'opacity-0'
              }`}
              loading="lazy"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = "/placeholder-image.png";
              }}
            />
          ))}
          {/* Image Indicators */}
          {data.images?.length > 1 && (
            <div className="absolute bottom-2 flex gap-1">
              {data.images.map((_, index) => (
                <div 
                  key={index}
                  className={`w-2 h-2 rounded-full ${
                    index === currentImage ? 'bg-white' : 'bg-gray-400'
                  }`}
                />
              ))}
            </div>
          )}
          
          {/* Wishlist Icon */}
          <div className="absolute top-2 left-2">
            {click ? (
              <AiFillHeart
                size={24}
                className="text-red-500 cursor-pointer hover:text-red-600 transition-colors duration-200"
                onClick={(e) => {
                  e.preventDefault(); // Prevent navigating when clicking the icon
                  removeFromWishlistHandler(data);
                }}
                aria-label="Remove from wishlist"
                title="Remove from wishlist"
                tabIndex={0}
                onKeyPress={(e) => {
                  if (e.key === "Enter") removeFromWishlistHandler(data);
                }}
              />
            ) : (
              <AiOutlineHeart
                size={24}
                className="text-gray-500 cursor-pointer hover:text-red-500 transition-colors duration-200"
                onClick={(e) => {
                  e.preventDefault(); // Prevent navigating when clicking the icon
                  addToWishlistHandler(data);
                }}
                aria-label="Add to wishlist"
                title="Add to wishlist"
                tabIndex={0}
                onKeyPress={(e) => {
                  if (e.key === "Enter") addToWishlistHandler(data);
                }}
              />
            )}
          </div>
        </div>
      </Link>

      {/* Content Section */}
      <div className="p-4 flex flex-col flex-1">
        <Link
          to={`/product/${data._id}`}
          className="flex-1"
          onClick={(e) => {
            if (isEvent) {
              e.preventDefault();
              window.location.href = `/product/${data._id}?isEvent=true`;
            }
          }}
        >
          {/* Product Name */}
          <h4 className="text-base sm:text-lg font-semibold mb-2 text-gray-800 hover:text-green-600 transition-colors duration-200">
            {data.name.length > 40 ? `${data.name.slice(0, 40)}...` : data.name}
          </h4>

          {/* Price Range and Sold Out */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2">
            <div className="flex items-center mb-2 sm:mb-0">
              <span className="text-sm sm:text-base font-medium text-gray-800">{getPriceRange()}</span>
              {data?.originalPriceWithCommission && (
                <span className="ml-2 text-xs sm:text-sm line-through text-gray-500">
                  Rs {data.originalPriceWithCommission}
                </span>
              )}
            </div>
            <span className="text-xs sm:text-sm font-medium text-green-600">
              {data?.sold_out} sold
            </span>
          </div>

          {/* Product Tags/Make */}
          <div>
            <span className="text-sm text-gray-700">Make: {data?.tags}</span>
          </div>
        </Link>

        {/*
        <div className="mt-4">
          <div className="flex items-center">
            <Ratings rating={data?.ratings} size="small" />
            <span className="ml-2 text-sm text-gray-600">({data?.ratings || 0})</span>
          </div>
        </div>
        */}

        {/* Distance Category or Error */}
        <div className="mt-2">
          {loadingDistance && (
            <div className="bg-gray-300 text-gray-600 text-xs px-2 py-1 rounded">
              Calculating...
            </div>
          )}
          {!loadingDistance && errorDistance && (
            <div className="bg-gray-500 text-gray-100 text-xs px-2 py-1 rounded">
              {errorDistance}
            </div>
          )}
          {!loadingDistance && !errorDistance && distanceCategory && (
            <div className="bg-gray-200 text-gray-700 text-xs px-2 py-1 rounded">
              {distanceCategory}
            </div>
          )}
        </div>
        {(!pinCode || !isServiceable) && (
    <div style={{
      padding: '8px',
      backgroundColor: '#fee2e2',
      color: '#dc2626',
      borderRadius: '6px',
      marginTop: '8px',
      fontSize: '14px',
      textAlign: 'center'
    }}>
      {!pinCode ? (
        "Please set your pincode to check availability"
      ) : (
        <>
          Not serviceable in your area. Click product to Enquire us to get this product.
        </>
      )}
    </div>
  )}
      </div>
    
    </div>
  );
};
export default ProductCard;

